import { Form, Formik, Field } from 'formik'
import { useTranslation } from 'react-i18next'
import { Share } from '../../../../../utils/Share'
import { useTw } from '../../../../../utils/Styles'
import { Avatars } from '../../../../../utils/Avatars'
import { useProfile } from '../../../../../utils/Profile'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/outline'
import { localizeString, isDark } from '../../../../../utils'
import { SubmitListener } from '../../../../../utils/SubmitListener'
import Wrapper, { LoadingWrapper } from '../../../../../utils/Wrapper'
import {
  KnowledgeBaseQuery,
  useKnowledgeBaseQuery,
} from '../KnowledgeBase.generated'

type Props = {
  article: KnowledgeBaseQuery['Profile']['knowledgeBase']['categories'][0]['articles'][0]
}

export function Article({ article }: Props) {
  const tw = useTw()
  const navigate = useNavigate()
  const { account } = useProfile()
  const [t] = useTranslation('common')
  const users = [
    article.createdBy,
    article.createdBy._id === article.publishedBy._id
      ? undefined
      : article.publishedBy,
  ]

  const shareBaseUrl = account?.clientPortal?.customDomain
    ? `https://${account?.clientPortal?.customDomain}`
    : `https://${account.slug}.firesquad.io`

  return (
    <div className={tw`rounded shadow-md border-t-2 border-foreground`}>
      <div className={tw`p-4 space-y-2`}>
        <button
          className={tw`cursor-pointer text-foreground`}
          onClick={() =>
            navigate(`/kb/${article.categoryId}/${article.articleId}`)
          }
        >
          {localizeString(article.title)}
        </button>
        <p
          title={localizeString(article.subtitle)}
          className={tw`line-clamp-2 text-neutral-400`}
        >
          {localizeString(article.subtitle)}
        </p>
        <div className={tw`flex justify-between items-center pt-4`}>
          <div className={tw`flex flex-row select-none`}>
            <Avatars users={users} />
            <div className={tw`ml-2`}>
              <p className={tw`text-sm text-neutral-400 font-bold`}>
                {t('home.kb_article.written_by')}
              </p>
              <p className={tw`text-sm text-neutral-350`}>
                {users
                  .filter((_) => _?._id)
                  .map((_) => _.name)
                  .join(' & ')}
              </p>
            </div>
          </div>
          {account?.clientPortal?.knowledgeBaseEnabled ? (
            <Share
              title={localizeString(article.title)}
              text={localizeString(article.subtitle)}
              url={`${shareBaseUrl}/category/${article.categoryId}/article/${article.articleId}`}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}

export function Articles() {
  const tw = useTw()
  const navigate = useNavigate()
  const { categoryId } = useParams()
  const [t] = useTranslation('common')
  const { data, loading } = useKnowledgeBaseQuery()
  const { foreground } = useProfile()

  if (loading) return <LoadingWrapper title={t('knowledge_base.title')} />

  const category = data?.Profile?.knowledgeBase?.categories.find(
    (_) => _._id === categoryId
  )

  return (
    <Wrapper
      title={t('knowledge_base.title')}
      subtitle={localizeString(category.title)}
    >
      <Formik
        initialValues={{ search: '' }}
        onSubmit={(values) =>
          values.search &&
          navigate('/kb-search', { state: { search: values.search } })
        }
      >
        <Form className={tw`flex px-4 mt-4 h-14`}>
          <Field
            name="search"
            type="text"
            placeholder={t('home.kb.search_placeholder')}
            className={tw`select-none my-2 w-full rounded-l border-r-0 focus:ring-foreground focus:border-foreground outline-none`}
          />
          <button
            type="submit"
            className={tw`leading-6 px-2 my-2 bg-foreground rounded-r`}
          >
            <ChevronRightIcon
              className={tw`h-6 w-6 ${
                isDark(foreground) ? 'text-white' : 'text-black'
              }`}
            />
          </button>
          <SubmitListener />
        </Form>
      </Formik>
      <div className={tw`my-4 space-y-4 px-4`}>
        {category.articles.map((_) => (
          <Article key={_._id} article={_} />
        ))}
      </div>
    </Wrapper>
  )
}
